import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  ShieldCheck, Star, CreditCard, MessageCircle, Users, Search,
  UserPlus, Eye, Calendar, Sparkles, AlertTriangle, ArrowRight, Check, Crown,
} from "lucide-react";
import { PUBLIC_BASE } from "../lib/publicBase";
import SeoHead from "../components/SeoHead";
import BrandMark from "../components/BrandMark";
import { SiteFooter } from "../components/Layout";

/**
 * ClientLanding — `/for/clients`
 *
 * The seeker-facing programmatic landing page. Distinct from the 17
 * practitioner-niche pages (which target supply-side signups); this one
 * targets demand-side: people looking for trusted spiritual help.
 *
 * Sections per spec:
 *   1. Hero
 *   2. Trust Section ("Spiritual Help Should Feel Safe")
 *   3. How It Works (4 steps)
 *   4. Practitioner Categories (15+ links to the niche pages)
 *   5. Why Clients Join Cauldron
 *   6. Safety Warning ("Never Pay Outside the Cauldron App")
 *   7. Social Proof / Testimonials
 *   8. Final CTA
 *   9. Footer (reuses SiteFooter)
 *
 * Canonical URL points at PUBLIC_BASE so this page can be linked from
 * paid ads / IG bios with proper SEO attribution.
 */
const TRUST_POINTS = [
  { icon: ShieldCheck, label: "Verified practitioner profiles" },
  { icon: Eye,         label: "Transparent identity and service listings" },
  { icon: Star,        label: "Ratings and reviews from real clients" },
  { icon: CreditCard,  label: "Secure in-app payments" },
  { icon: MessageCircle, label: "No pressure to pay outside the platform" },
];

const STEPS = [
  { icon: UserPlus, n: 1, title: "Create Your Free Account",
    body: "Sign up as a seeker and start exploring trusted spiritual practitioners." },
  { icon: Search, n: 2, title: "Search by Spiritual Service",
    body: "Find practitioners for tarot, Reiki, astrology, Ifá, Hoodoo, rootwork, mediumship, spiritual coaching, energy healing, and more." },
  { icon: Eye, n: 3, title: "Review Profiles Before You Book",
    body: "View experience, trust level, services, ratings, photos, videos, and client reviews." },
  { icon: Calendar, n: 4, title: "Book and Pay Safely",
    body: "Use the services tab or custom payment option directly inside Cauldron." },
];

// Mirrors lib/niches.js — kept inline here so the categories grid stays
// stable even if the practitioner-side niche set changes.
const CATEGORIES = [
  { slug: "tarot-readers",              label: "Tarot Readers" },
  { slug: "psychic-mediums",            label: "Psychic Mediums" },
  { slug: "reiki-masters",              label: "Reiki Masters" },
  { slug: "energy-healers",             label: "Energy Healers" },
  { slug: "astrologers",                label: "Astrologers" },
  { slug: "hoodoo-practitioners",       label: "Hoodoo Practitioners" },
  { slug: "rootworkers",                label: "Rootworkers" },
  { slug: "ifa-priests",                label: "Ifá Priests" },
  { slug: "lucumi-priests",             label: "Lucumí Practitioners" },
  { slug: "palo-mayombe-practitioners", label: "Palo Mayombe Practitioners" },
  { slug: "vodou-priests",              label: "Vodou Practitioners" },
  { slug: "spiritual-coaches",          label: "Spiritual Coaches" },
  { slug: "crystal-healers",            label: "Crystal Healers" },
  { slug: "hypnotherapists",            label: "Hypnotherapists" },
];

const BENEFITS = [
  "Find spiritual workers in one place",
  "Compare practitioners before booking",
  "Avoid unverified social-media scammers",
  "Communicate directly through the app",
  "Pay securely for services",
  "Support real spiritual professionals",
];

const TESTIMONIALS = [
  { quote: "Cauldron helped me find someone I felt comfortable trusting.", attribution: "Early Cauldron Seeker" },
  { quote: "I liked being able to see services, reviews, and verification before booking.", attribution: "Early Cauldron Seeker" },
  { quote: "It felt safer than sending money to random people on social media.", attribution: "Early Cauldron Seeker" },
];

export default function ClientLanding() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const canonical = `${PUBLIC_BASE}/for/clients`;
  const seoTitle = "Find Verified Spiritual Practitioners You Can Trust | Cauldron";
  const seoDesc = "Cauldron connects you with authentic, transparent, and verified spiritual practitioners for readings, energy work, rituals, coaching, and spiritual services — all in one secure app.";
  const seoKeywords = "verified spiritual practitioner, tarot reading, Reiki, Ifá, Hoodoo, rootwork, psychic medium, energy healer, astrology, spiritual coach, find tarot reader, find Babalawo, spiritual marketplace, trusted spiritual app";

  return (
    <div className="dark-theme min-h-screen relative overflow-hidden" data-testid="client-landing">
      <SeoHead title={seoTitle} description={seoDesc} keywords={seoKeywords} canonical={canonical} ogImage={`${PUBLIC_BASE}/brand/cauldron-share.png`} />

      {/* Nav */}
      <nav className="relative z-20 max-w-[1600px] mx-auto px-6 sm:px-8 py-5 flex justify-between items-center" data-testid="client-nav">
        <Link to="/" className="flex items-center gap-3" data-testid="client-brand-link">
          <BrandMark size={36} />
          <span className="font-serif text-2xl tracking-wide">cauldron</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-4">
          <Link to="/auth?mode=login" className="text-xs sm:text-sm tracking-wider uppercase text-foreground/80 hover:text-secondary" data-testid="client-login-link">Sign In</Link>
          <Link to="/auth?mode=register&role=SEEKER&utm_source=landing&utm_campaign=clients"
            className="text-xs sm:text-sm tracking-wider uppercase bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:bg-primary/90"
            data-testid="client-register-link">
            Join Now
          </Link>
        </div>
      </nav>

      {/* ─────── HERO ─────── */}
      <section className="relative z-10" data-testid="client-hero">
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-mystic text-secondary mb-5">For seekers</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6" data-testid="client-headline">
              Find Verified Spiritual Practitioners You Can Trust
            </h1>
            <p className="text-base sm:text-lg text-foreground/75 max-w-xl leading-relaxed mb-10 font-light" data-testid="client-subhead">
              Cauldron connects you with authentic, transparent, and verified spiritual practitioners for readings, energy work, rituals, coaching, and spiritual services — all in one secure app.
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <Link to="/auth?mode=register&role=SEEKER&utm_source=landing&utm_campaign=clients&utm_medium=hero_primary"
                className="bg-primary text-primary-foreground px-7 py-3.5 text-xs sm:text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-primary/90 inline-flex items-center gap-2"
                data-testid="client-cta-primary">
                Join Cauldron Free <ArrowRight size={14} />
              </Link>
              <Link to="/marketplace"
                className="border border-secondary/50 text-secondary px-7 py-3.5 text-xs sm:text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-secondary/10"
                data-testid="client-cta-secondary">
                Browse Verified Practitioners
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.18em] text-foreground/55">
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-secondary" /> Identity verified</div>
              <div className="flex items-center gap-2"><Star size={14} className="text-secondary" /> Real client reviews</div>
              <div className="flex items-center gap-2"><CreditCard size={14} className="text-secondary" /> Secure payments</div>
            </div>
          </div>
          {/* Hero visual mockup */}
          <div className="relative" data-testid="client-hero-visual">
            <div className="parchment rounded-lg border border-secondary/20 overflow-hidden shadow-2xl">
              <img
                src="/screens/landing-profile.jpeg"
                alt="A seeker browsing a verified practitioner profile on Cauldron — showing ratings, services, and a secure payment button."
                className="w-full bg-purple-950/40"
                onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────── TRUST ─────── */}
      <section className="relative z-10 border-t border-secondary/15 bg-background/60" data-testid="client-trust">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic mb-5">The problem</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-6 max-w-3xl">
            Spiritual help should feel safe.
          </h2>
          <p className="text-base sm:text-lg text-foreground/75 max-w-3xl leading-relaxed font-light mb-12">
            Too many clients waste money on unverified people online who disappear, overcharge, or make false promises. Cauldron was built to bring visibility, accountability, and trust back into the spiritual community.
          </p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            {TRUST_POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <li key={i} className="flex items-start gap-3" data-testid={`trust-${i}`}>
                  <Icon size={18} className="text-secondary shrink-0 mt-0.5" strokeWidth={1.75} />
                  <span className="text-sm text-foreground/85">{p.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ─────── HOW IT WORKS ─────── */}
      <section className="relative z-10 border-t border-secondary/15" data-testid="client-how">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic text-secondary mb-5">How it works</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-12 max-w-xl">
            Four steps from clarity to booking.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-secondary/15 border border-secondary/15 rounded-sm overflow-hidden">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="bg-background p-6 sm:p-7" data-testid={`step-${s.n}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-secondary/15 text-secondary font-serif text-sm">
                      {s.n}
                    </span>
                    <Icon size={20} className="text-secondary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-lg mb-2 leading-tight">{s.title}</h3>
                  <p className="text-sm text-foreground/65 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────── PRACTITIONER CATEGORIES ─────── */}
      <section className="relative z-10 border-t border-secondary/15 bg-background/60" data-testid="client-categories">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic mb-5">Traditions</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-3">
            Find trusted spiritual workers including:
          </h2>
          <p className="text-sm text-foreground/55 mb-10">Tap a tradition to browse verified practitioners.</p>
          <div className="flex flex-wrap gap-2.5">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/for/${c.slug}`}
                className="border border-secondary/30 hover:border-secondary text-secondary hover:bg-secondary/10 px-4 py-2 rounded-full text-sm transition-colors"
                data-testid={`category-${c.slug}`}
              >
                {c.label}
              </Link>
            ))}
            <span className="text-sm text-foreground/55 px-4 py-2">and many more…</span>
          </div>
        </div>
      </section>

      {/* ─────── WHY CLIENTS JOIN ─────── */}
      <section className="relative z-10 border-t border-secondary/15" data-testid="client-why">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic text-secondary mb-5">The Cauldron promise</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-6 max-w-2xl">
            Stop guessing who to trust.
          </h2>
          <p className="text-base sm:text-lg text-foreground/75 max-w-2xl leading-relaxed font-light mb-12">
            Cauldron helps you choose spiritual help with confidence by giving you more information before you spend your money.
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-foreground/80" data-testid={`benefit-${i}`}>
                <Check size={18} className="shrink-0 mt-0.5 text-secondary" strokeWidth={2.25} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─────── SAFETY WARNING ─────── */}
      <section className="relative z-10 border-t border-secondary/15 bg-red-950/15" data-testid="client-safety">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <div className="flex items-start gap-4">
            <AlertTriangle size={32} className="text-red-300 shrink-0 mt-1" strokeWidth={1.5} />
            <div>
              <div className="label-mystic text-red-300 mb-3">Safety first</div>
              <h2 className="font-serif text-2xl sm:text-3xl leading-tight mb-4">
                Never pay outside the Cauldron app.
              </h2>
              <p className="text-sm sm:text-base text-foreground/80 leading-relaxed mb-3">
                For your protection, all payments should be made through the practitioner&apos;s services tab or the custom payment option inside Cauldron. If someone asks you to pay outside the app, that is a red flag and you should report them to the Cauldron Admin immediately.
              </p>
              <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                Cauldron is designed to help protect seekers from scams, harassment, and fake practitioners.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-red-300 hover:text-red-200 text-xs uppercase tracking-[0.18em] mt-5"
                data-testid="client-report-link"
              >
                Report a practitioner <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─────── SOCIAL PROOF ─────── */}
      <section className="relative z-10 border-t border-secondary/15" data-testid="client-testimonials">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="label-mystic text-secondary mb-5">Real seekers</div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-3 max-w-2xl">
            Built for seekers who want real spiritual help.
          </h2>
          <p className="text-base sm:text-lg text-foreground/75 max-w-3xl leading-relaxed font-light mb-12">
            Whether you need clarity, healing, protection, guidance, spiritual coaching, or ritual work, Cauldron gives you access to practitioners who are willing to be visible, accountable, and transparent.
          </p>
          <div className="grid sm:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <blockquote
                key={i}
                className="parchment border border-secondary/15 rounded-sm p-6 flex flex-col"
                data-testid={`client-testimonial-${i}`}
              >
                <Sparkles size={16} className="text-secondary mb-3" />
                <p className="text-sm text-foreground/85 leading-relaxed italic mb-4">&ldquo;{t.quote}&rdquo;</p>
                <footer className="mt-auto text-[11px] uppercase tracking-[0.18em] text-foreground/55">
                  — {t.attribution}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── FINAL CTA ─────── */}
      <section className="relative z-10 border-t border-secondary/15 bg-gradient-to-b from-background to-purple-950/25" data-testid="client-cta-final">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-28 text-center">
          <Crown size={36} className="text-secondary mx-auto mb-6" strokeWidth={1.25} />
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] mb-5">
            Ready to find a verified spiritual practitioner?
          </h2>
          <p className="text-base sm:text-lg text-foreground/75 mb-10 font-light max-w-xl mx-auto">
            Join Cauldron today and connect with trusted spiritual professionals from different traditions.
          </p>
          <Link
            to="/auth?mode=register&role=SEEKER&utm_source=landing&utm_campaign=clients&utm_medium=cta_final"
            className="bg-primary text-primary-foreground px-8 py-4 text-xs sm:text-sm uppercase tracking-[0.2em] rounded-sm hover:bg-primary/90 inline-flex items-center gap-2"
            data-testid="client-cta-join"
          >
            Join Cauldron Free <ArrowRight size={14} />
          </Link>
          <p className="mt-6 text-xs sm:text-sm text-foreground/55">
            Create your free seeker account and start browsing verified practitioners today.
          </p>
          <div className="mt-10 text-[10px] uppercase tracking-[0.2em] text-foreground/40 flex flex-wrap items-center justify-center gap-3">
            <span><Check size={10} className="inline -mt-0.5 mr-1" />No credit card required</span>
            <span>·</span>
            <span><Users size={10} className="inline -mt-0.5 mr-1" />500 founder practitioners</span>
            <span>·</span>
            <span><MessageCircle size={10} className="inline -mt-0.5 mr-1" />Real human support</span>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
